import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import style from './customer-details-drawer.module.less';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { useModal } from '../../hooks/use-modal';
import { customerToModalInitialData } from '../../pages/customers-page/helpers/customer-form-helpers';
import { CreateCustomerModal } from '../../pages/customers-page/modals/create-customer-modal';
import { launchCustomersCreateLabelModal } from '../../store/customers/create-label/customers.create-label.actions';
import {
  addTagsToCustomers,
  removeTagsFromCustomers,
  closeDetailsModal,
  launchCustomerMarketplaceUsernamesModal,
} from '../../store/customers/customers.actions';
import Global from '../../store/global-selectors';
import { Button } from '../../ui-components/components/button';
import { Drawer } from '../../ui-components/components/drawer/drawer';
import { DrawerHeader } from '../../ui-components/components/drawer/drawer-header';
import { Text } from '../../ui-components/components/text/text';
import { TextType } from '../../ui-components/components/text/text-type';
import { CustomerInformation } from './components/customer-information';
import { CustomerPreviousOrders } from './components/customer-previous-orders';
import messages from './messages';

/**
 * Customer details drawer component
 * @param {object} props
 * @param {string} props.customerId - The customer ID
 * @param {object} props.modalFrameworkMethods - Modal framework methods with closeModal function
 * @returns {React.ReactElement}
 */
export const CustomerDetailsDrawer = ({ customerId, modalFrameworkMethods }) => {
  const dispatch = useDispatch();
  const { open: openEditModal, isOpen: isEditModalOpen, close: closeEditModal } = useModal();
  const hasCustomerManagementRole = useSelector(Global.selectUserhasCustomerManagementRole);
  const hasShippingRole = useSelector(Global.selectUserhasShippingRole);

  const customerDetails = useSelector((state) => Global.selectCustomersGridCustomerById(state, { customerId }) || emptyObject);
  const tags = useSelector(Global.selectSellerTags) || emptyArray;

  const editModalInitialData = useMemo(() => customerToModalInitialData(customerDetails), [customerDetails]);

  const selectedTags = useMemo(() => {
    return tags.filter((tag) => customerDetails.tagIds?.includes(tag.tagId));
  }, [tags, customerDetails.tagIds]);

  const addTagToCustomers = useCallback(
    (tag) => {
      dispatch(addTagsToCustomers([customerId], tag.tagId));
    },
    [dispatch, customerId],
  );

  const removeTagFromCustomers = useCallback(
    (tag) => {
      dispatch(removeTagsFromCustomers([customerId], tag.tagId));
    },
    [dispatch, customerId],
  );

  const openCustomersCreateLabelModal = useCallback(() => {
    dispatch(launchCustomersCreateLabelModal());
  }, [dispatch]);

  const openCustomerMarketplaceUsernamesModal = useCallback(() => {
    dispatch(launchCustomerMarketplaceUsernamesModal(customerDetails.marketplaces));
  }, [dispatch, customerDetails.marketplaces]);

  const handleEditCustomer = useCallback(() => {
    openEditModal();
  }, [openEditModal]);

  const closeDrawer = useCallback(() => {
    dispatch(closeDetailsModal());
    modalFrameworkMethods.closeModal();
  }, [dispatch, modalFrameworkMethods]);

  const onTagsToggled = useCallback(
    (tag) => {
      const tagExists = selectedTags.some((selectedTag) => selectedTag.tagId === tag.tagId);
      if (tagExists) {
        removeTagFromCustomers(tag);
      } else {
        addTagToCustomers(tag);
      }
    },
    [selectedTags, addTagToCustomers, removeTagFromCustomers],
  );

  return (
    <Drawer canvasClassName={style['drawer-canvas']} onCancel={closeDrawer} onClose={closeDrawer}>
      <DrawerHeader closeButtonAriaLabel={messages.closeDrawer} title={<Text textType={TextType.H3} value={messages.drawerTitle} />} />
      <div className={style['drawer-content']}>
        <div className={style['customer-info-section']} data-testid='customer-info-section'>
          <CustomerInformation
            closeModal={closeDrawer}
            customerDetails={customerDetails}
            isEditEnabled={hasCustomerManagementRole}
            onEditCustomer={handleEditCustomer}
            onTagsToggled={onTagsToggled}
            selectedTags={selectedTags}
            showMarketPlaceUsernames={openCustomerMarketplaceUsernamesModal}
            tags={tags}
          />
        </div>
        <div className={style['previous-orders-section']} data-testid='previous-orders-section'>
          <CustomerPreviousOrders containerClassName={style['order-items-container']} wrapperClassName={style['orders-wrapper']} />
        </div>
      </div>
      <div className={style['drawer-footer']}>
        <Button disabled={!hasShippingRole} onClick={() => openCustomersCreateLabelModal(customerDetails.address)} theme='primary'>
          <Text textType={TextType.Caption} value={messages.createLabel} />
        </Button>
      </div>
      {isEditModalOpen ? (
        <CreateCustomerModal
          customerId={customerId}
          initialData={editModalInitialData}
          modalFrameworkMethods={{ closeModal: closeEditModal }}
          mode='edit'
          source='customer_drawer'
        />
      ) : null}
    </Drawer>
  );
};
